import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic,
  Play,
  Pause,
  Upload,
  PhoneCall,
  Users,
  TrendingUp,
  Clock,
  DollarSign,
  Calendar,
  Filter,
  Search,
  Plus,
  Eye,
  Settings,
  Download,
  BarChart3,
  Send,
  Loader2,
  ArrowUp,
  ArrowDown,
  Activity,
  CheckCircle2,
  AlertCircle,
  X,
  Edit,
  Archive,
  Trash2,
  MoreVertical,
  Volume2,
  Target,
  Zap,
  Star,
  Share,
  FileAudio,
  FileText,
  Camera,
  Globe,
  Bell,
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';

// Types for Voicemail Campaigns
interface VoicemailCampaign {
  id: string;
  name: string;
  audioFileUrl: string;
  audioDuration: number;
  callerId: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'paused' | 'cancelled' | 'completed';
  totalRecipients: number;
  dropsSent: number;
  dropsDelivered: number;
  dropsFailed: number;
  estimatedCost: number;
  actualCost: number;
  scheduledAt?: Date;
  completedAt?: Date;
  createdAt: Date;
  provider?: VoicemailProvider;
}

interface VoicemailProvider {
  id: string;
  name: string;
  providerType: 'slybroadcast' | 'ringlessvm' | 'drop_cowboy' | 'custom';
  status: 'active' | 'inactive' | 'suspended';
  dailyLimit: number;
  currentUsage: number;
  costPerDrop: number;
}

interface VoicemailDrop {
  id: string;
  recipientPhone: string;
  status: 'queued' | 'sent' | 'delivered' | 'failed' | 'cancelled';
  cost: number;
  durationPlayed: number;
  carrierDetected?: string;
  sentAt?: Date;
  deliveredAt?: Date;
  errorMessage?: string;
}

interface AudioFile {
  fileId: string;
  fileName: string;
  fileSize: number;
  duration: number;
  format: string;
  url: string;
  createdAt: Date;
}

interface CampaignAnalytics {
  campaignId: string;
  deliveryRate: number;
  avgPlaybackDuration: number;
  totalCost: number;
  carrierBreakdown: Record<string, { delivered: number; failed: number; rate: number }>;
  timeBreakdown: Record<string, number>;
  costAnalysis: {
    costPerDelivery: number;
    costPerSecondPlayed: number;
    roi: number;
  };
}

interface DashboardStats {
  totalCampaigns: number;
  activeCampaigns: number;
  totalDropsSent: number;
  totalDelivered: number;
  overallDeliveryRate: number;
  totalCostSpent: number;
  avgCostPerDrop: number;
}

// API Service
class VoicemailCampaignService {
  private baseUrl = '/api/rvm';

  async getCampaigns(params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${this.baseUrl}/campaigns?${queryParams}`);
    return response.json();
  }

  async getCampaign(id: string): Promise<VoicemailCampaign> {
    const response = await fetch(`${this.baseUrl}/campaigns/${id}`);
    return response.json();
  }

  async createCampaign(campaignData: {
    name: string;
    audioFileUrl: string;
    audioDuration: number;
    callerId: string;
    recipients: Array<{ phoneNumber: string; name?: string }>;
  }) {
    const response = await fetch(`${this.baseUrl}/campaigns`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(campaignData),
    });
    return response.json();
  }

  async updateCampaign(id: string, updates: Partial<VoicemailCampaign>) {
    const response = await fetch(`${this.baseUrl}/campaigns/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    return response.json();
  }

  async getCampaignAnalytics(id: string): Promise<CampaignAnalytics> {
    const response = await fetch(`${this.baseUrl}/campaigns/${id}/analytics`);
    return response.json();
  }

  async getProviders(): Promise<VoicemailProvider[]> {
    const response = await fetch(`${this.baseUrl}/providers`);
    return response.json();
  }

  async getDropStatus(dropId: string) {
    const response = await fetch(`${this.baseUrl}/drops/${dropId}/status`);
    return response.json();
  }

  async uploadAudioFile(file: File): Promise<AudioFile> {
    const formData = new FormData();
    formData.append('audio', file);

    const response = await fetch('/api/voicemail/upload', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload audio file');
    }

    const result = await response.json();
    return result.data;
  }

  async getAudioFiles(): Promise<AudioFile[]> {
    const response = await fetch('/api/voicemail/files');
    const result = await response.json();
    return result.data || [];
  }

  async getDashboardStats(): Promise<DashboardStats> {
    const response = await fetch(`${this.baseUrl}/analytics/dashboard`);
    return response.json();
  }
}

// Components
const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
  trend?: { value: number; direction: 'up' | 'down' };
}> = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <motion.div
    className="bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl p-6 hover:border-neutral-700/50 transition-all duration-300"
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex items-start justify-between mb-4">
      <div className={cn("p-3 rounded-xl bg-gradient-to-br", color)}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      {trend && (
        <div className={cn(
          "flex items-center text-sm font-medium",
          trend.direction === 'up' ? 'text-green-400' : 'text-red-400'
        )}>
          {trend.direction === 'up' ? <ArrowUp className="w-4 h-4 mr-1" /> : <ArrowDown className="w-4 h-4 mr-1" />}
          {trend.value}%
        </div>
      )}
    </div>
    <div className="space-y-1">
      <h3 className="text-2xl font-bold text-white">{value}</h3>
      <p className="text-sm text-neutral-400">{title}</p>
      {subtitle && <p className="text-xs text-neutral-500">{subtitle}</p>}
    </div>
  </motion.div>
);

const CampaignCard: React.FC<{
  campaign: VoicemailCampaign;
  onView: () => void;
  onEdit: () => void;
  onArchive: () => void;
}> = ({ campaign, onView, onEdit, onArchive }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-400 bg-green-400/10';
      case 'sending': return 'text-blue-400 bg-blue-400/10';
      case 'scheduled': return 'text-yellow-400 bg-yellow-400/10';
      case 'paused': return 'text-orange-400 bg-orange-400/10';
      case 'cancelled': return 'text-red-400 bg-red-400/10';
      default: return 'text-neutral-400 bg-neutral-400/10';
    }
  };

  const getDeliveryRate = () => {
    return campaign.totalRecipients > 0 
      ? ((campaign.dropsDelivered / campaign.totalRecipients) * 100).toFixed(1)
      : '0';
  };

  return (
    <motion.div
      className="bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl p-6 hover:border-neutral-700/50 transition-all duration-300"
      whileHover={{ scale: 1.01, y: -2 }}
      whileTap={{ scale: 0.99 }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Mic className="w-6 h-6 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-semibold text-white truncate">{campaign.name}</h3>
            <p className="text-sm text-neutral-400">
              {campaign.audioDuration}s • {campaign.callerId}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <span className={cn("px-3 py-1 rounded-lg text-xs font-medium", getStatusColor(campaign.status))}>
            {campaign.status}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="text-neutral-400 hover:text-white"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{campaign.totalRecipients.toLocaleString()}</p>
          <p className="text-xs text-neutral-400">Recipients</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-400">{getDeliveryRate()}%</p>
          <p className="text-xs text-neutral-400">Delivered</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-blue-400">${campaign.actualCost.toFixed(2)}</p>
          <p className="text-xs text-neutral-400">Cost</p>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-xs text-neutral-400">
          <Calendar className="w-3 h-3" />
          <span>Created {new Date(campaign.createdAt).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={onView}
            className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
          >
            <Eye className="w-4 h-4 mr-1" />
            View
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

const AudioUploader: React.FC<{
  onUploadComplete: (audioFile: AudioFile) => void;
  onClose: () => void;
}> = ({ onUploadComplete, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const [loading, setLoading] = useState(false);

  const voicemailService = new VoicemailCampaignService();

  const loadAudioFiles = useCallback(async () => {
    setLoading(true);
    try {
      const files = await voicemailService.getAudioFiles();
      setAudioFiles(files);
    } catch (error) {
      console.error('Failed to load audio files:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAudioFiles();
  }, [loadAudioFiles]);

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    try {
      const audioFile = await voicemailService.uploadAudioFile(file);
      onUploadComplete(audioFile);
      await loadAudioFiles();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('audio/')) {
      handleFileUpload(file);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 border border-neutral-700 rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Audio Library</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-neutral-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Upload Area */}
          <div
            className={cn(
              "border-2 border-dashed rounded-xl p-8 text-center transition-colors",
              dragActive ? "border-blue-500 bg-blue-500/5" : "border-neutral-600",
              uploading && "opacity-50 pointer-events-none"
            )}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            {uploading ? (
              <div className="space-y-4">
                <Loader2 className="w-12 h-12 text-blue-400 mx-auto animate-spin" />
                <p className="text-neutral-400">Uploading audio file...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <FileAudio className="w-12 h-12 text-neutral-400 mx-auto" />
                <div>
                  <p className="text-white font-medium">Drop audio file here or click to browse</p>
                  <p className="text-sm text-neutral-400 mt-1">
                    Supports MP3, WAV, AAC, M4A (max 10MB, 2 minutes)
                  </p>
                </div>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="audio-upload"
                />
                <label htmlFor="audio-upload">
                  <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </Button>
                </label>
              </div>
            )}
          </div>

          {/* Existing Audio Files */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-white mb-4">Existing Audio Files</h3>
            {loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 text-blue-400 mx-auto animate-spin" />
                <p className="text-neutral-400 mt-2">Loading audio files...</p>
              </div>
            ) : audioFiles.length > 0 ? (
              <div className="space-y-3">
                {audioFiles.map((file) => (
                  <div
                    key={file.fileId}
                    className="flex items-center justify-between p-4 bg-neutral-800/50 rounded-xl border border-neutral-700/50"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                        <FileAudio className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{file.fileName}</p>
                        <p className="text-sm text-neutral-400">
                          {file.duration}s • {(file.fileSize / 1024 / 1024).toFixed(1)}MB
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onUploadComplete(file)}
                      className="bg-neutral-700/50 border-neutral-600/50 text-neutral-300 hover:bg-neutral-600/50"
                    >
                      Select
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <FileAudio className="w-12 h-12 text-neutral-500 mx-auto mb-4" />
                <p className="text-neutral-400">No audio files uploaded yet</p>
                <p className="text-sm text-neutral-500 mt-1">Upload your first audio file to get started</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const CreateCampaignModal: React.FC<{
  onClose: () => void;
  onCreate: (campaign: any) => void;
}> = ({ onClose, onCreate }) => {
  const [step, setStep] = useState(1);
  const [campaignData, setCampaignData] = useState({
    name: '',
    audioFileUrl: '',
    audioDuration: 0,
    callerId: '',
    recipients: [] as Array<{ phoneNumber: string; name?: string }>,
  });
  const [showAudioUploader, setShowAudioUploader] = useState(false);
  const [creating, setCreating] = useState(false);

  const voicemailService = new VoicemailCampaignService();

  const handleCreateCampaign = async () => {
    setCreating(true);
    try {
      const campaign = await voicemailService.createCampaign(campaignData);
      onCreate(campaign);
      onClose();
    } catch (error) {
      console.error('Failed to create campaign:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleAudioSelect = (audioFile: AudioFile) => {
    setCampaignData(prev => ({
      ...prev,
      audioFileUrl: audioFile.url,
      audioDuration: audioFile.duration,
    }));
    setShowAudioUploader(false);
  };

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 border border-neutral-700 rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Create Voicemail Campaign</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="text-neutral-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Step Indicator */}
            <div className="flex items-center justify-center space-x-4 mb-8">
              {[1, 2, 3].map((stepNum) => (
                <div key={stepNum} className="flex items-center">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                    step >= stepNum 
                      ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                      : "bg-neutral-700 text-neutral-400"
                  )}>
                    {stepNum}
                  </div>
                  {stepNum < 3 && (
                    <div className={cn(
                      "w-12 h-0.5 mx-2",
                      step > stepNum ? "bg-gradient-to-r from-blue-500 to-purple-600" : "bg-neutral-700"
                    )} />
                  )}
                </div>
              ))}
            </div>

            {/* Step Content */}
            {step === 1 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white">Campaign Details</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Campaign Name
                    </label>
                    <input
                      type="text"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter campaign name..."
                      className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-300 mb-2">
                      Caller ID
                    </label>
                    <input
                      type="text"
                      value={campaignData.callerId}
                      onChange={(e) => setCampaignData(prev => ({ ...prev, callerId: e.target.value }))}
                      placeholder="Enter caller ID..."
                      className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white">Audio Message</h3>
                <div className="space-y-4">
                  {campaignData.audioFileUrl ? (
                    <div className="p-4 bg-neutral-800/50 rounded-xl border border-neutral-700/50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
                            <FileAudio className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Audio Selected</p>
                            <p className="text-sm text-neutral-400">{campaignData.audioDuration}s duration</p>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowAudioUploader(true)}
                          className="bg-neutral-700/50 border-neutral-600/50 text-neutral-300 hover:bg-neutral-600/50"
                        >
                          Change
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowAudioUploader(true)}
                      className="w-full p-8 border-2 border-dashed border-neutral-600 rounded-xl text-center hover:border-blue-500 hover:bg-blue-500/5 transition-colors"
                    >
                      <FileAudio className="w-12 h-12 text-neutral-400 mx-auto mb-4" />
                      <p className="text-white font-medium">Select Audio File</p>
                      <p className="text-sm text-neutral-400 mt-1">Choose from library or upload new</p>
                    </button>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-white">Recipients</h3>
                <div className="space-y-4">
                  <textarea
                    placeholder="Enter phone numbers (one per line) or upload CSV..."
                    rows={8}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white placeholder-neutral-400 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-neutral-400">0 recipients added</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-neutral-700/50 border-neutral-600/50 text-neutral-300 hover:bg-neutral-600/50"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload CSV
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              <Button
                variant="outline"
                onClick={() => step > 1 ? setStep(step - 1) : onClose()}
                className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
              >
                {step > 1 ? 'Previous' : 'Cancel'}
              </Button>
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={
                    (step === 1 && (!campaignData.name || !campaignData.callerId)) ||
                    (step === 2 && !campaignData.audioFileUrl)
                  }
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  Next
                </Button>
              ) : (
                <Button
                  onClick={handleCreateCampaign}
                  disabled={creating || campaignData.recipients.length === 0}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Create Campaign
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {showAudioUploader && (
        <AudioUploader
          onUploadComplete={handleAudioSelect}
          onClose={() => setShowAudioUploader(false)}
        />
      )}
    </>
  );
};

// Main Voicemail Campaigns Component
export const VoicemailCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<VoicemailCampaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<VoicemailCampaign | null>(null);
  const [analytics, setAnalytics] = useState<CampaignAnalytics | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0,
  });

  const voicemailService = new VoicemailCampaignService();

  // Load campaigns
  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const result = await voicemailService.getCampaigns({
        page: pagination.page,
        limit: pagination.limit,
        ...filters,
      });
      setCampaigns(result.campaigns || []);
      setPagination(prev => ({
        ...prev,
        total: result.pagination?.total || 0,
        pages: result.pagination?.pages || 0,
      }));
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Load dashboard stats
  const loadDashboardStats = useCallback(async () => {
    try {
      const stats = await voicemailService.getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Failed to load dashboard stats:', error);
    }
  }, []);

  // Load campaign details
  const loadCampaignDetails = async (campaignId: string) => {
    try {
      const campaign = await voicemailService.getCampaign(campaignId);
      const campaignAnalytics = await voicemailService.getCampaignAnalytics(campaignId);
      
      setSelectedCampaign(campaign);
      setAnalytics(campaignAnalytics);
    } catch (error) {
      console.error('Failed to load campaign details:', error);
    }
  };

  const handleCreateCampaign = async (campaign: any) => {
    await loadCampaigns();
    await loadDashboardStats();
  };

  useEffect(() => {
    loadCampaigns();
    loadDashboardStats();
  }, [loadCampaigns, loadDashboardStats]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-500 bg-clip-text text-transparent">
              Voicemail Campaigns
            </h1>
            <p className="text-neutral-400 mt-1">Create and manage ringless voicemail campaigns</p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              className="bg-neutral-800/50 border-neutral-700/50 text-neutral-300 hover:bg-neutral-700/50"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Campaign
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {dashboardStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Campaigns"
              value={dashboardStats.totalCampaigns.toLocaleString()}
              subtitle={`${dashboardStats.activeCampaigns} active`}
              icon={Mic}
              color="from-purple-500 to-purple-600"
              trend={{ value: 15, direction: 'up' }}
            />
            <StatCard
              title="Drops Sent"
              value={dashboardStats.totalDropsSent.toLocaleString()}
              subtitle={`${dashboardStats.totalDelivered} delivered`}
              icon={Send}
              color="from-blue-500 to-blue-600"
              trend={{ value: 8, direction: 'up' }}
            />
            <StatCard
              title="Delivery Rate"
              value={`${dashboardStats.overallDeliveryRate.toFixed(1)}%`}
              subtitle="Last 30 days"
              icon={Target}
              color="from-green-500 to-green-600"
              trend={{ value: 3, direction: 'up' }}
            />
            <StatCard
              title="Total Spent"
              value={`$${dashboardStats.totalCostSpent.toFixed(2)}`}
              subtitle={`$${dashboardStats.avgCostPerDrop.toFixed(3)} per drop`}
              icon={DollarSign}
              color="from-orange-500 to-orange-600"
              trend={{ value: 2, direction: 'down' }}
            />
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-gradient-to-br from-neutral-900/50 via-neutral-800/30 to-neutral-900/50 backdrop-blur-xl border border-neutral-800/50 rounded-2xl p-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent"
              />
            </div>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="px-4 py-3 bg-neutral-800/50 border border-neutral-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="sending">Sending</option>
              <option value="completed">Completed</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Campaigns Grid */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">
              Campaigns ({campaigns.length})
            </h2>
            {loading && <Loader2 className="w-5 h-5 animate-spin text-purple-400" />}
          </div>
          
          {campaigns.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.id}
                  campaign={campaign}
                  onView={() => loadCampaignDetails(campaign.id)}
                  onEdit={() => {
                    // Handle edit
                  }}
                  onArchive={() => {
                    // Handle archive
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Mic className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No campaigns found</h3>
              <p className="text-neutral-400 mb-6">Create your first voicemail campaign to get started</p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <CreateCampaignModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateCampaign}
        />
      )}
    </div>
  );
};

export default VoicemailCampaigns; 